import * as yup from "yup";
import dayjs from "dayjs";
import {SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";
import {fromGenericBeToGenericFe, fromGenericFeToGenericBe, i18nConvertNumberWithDecimals} from "../../../utils";
import {
    FormOWAncillaryModel,
    OWAncillaryEntityRequest,
    OWAncillaryEntityResponse
} from "../../../api/types/owAncillary";
import {AncillaryEntityResponse, TaxCodeObject} from "../../../api/types/ancillary";
import {yupPickupFromFunc, yupPickupToFunc, yupReservationFromFunc, yupReservationToFunc} from "../../../yupUtils";
import {maxNumberYupLimit} from "../../../constants";

export const initialOWAncillaryForm = (legalEntityOptions: SelectElementModelFE[], dealerOption: SelectElementModelFE[]) => ({
    note: null,
    reservationFrom: "",
    reservationTo: "",
    pickupFrom: "",
    pickupTo: "",
    tpUnit: null,
    currency: "EUR",
    minimumAmount: null,
    maximumAmount: null,
    chargingWay: null,
    maxUnits: null,
    chargeType: null,
    nameAncillary: "",
    code: "",
    description: "",
    type: "",
    subtype: "",
    segments: [],
    cardinality: null,
    sellDesk: false,
    applicationTiming: null,
    toleranceTime: null,
    additionalField: null,
    tax: false,
    taxCode: "",
    dealer: dealerOption.length === 1 ? dealerOption[0] : null,
    legalEntity: legalEntityOptions.length === 1 ? legalEntityOptions[0] : null
});

export const getOWAncillaryBaseDefinitionValidationSchema = (t: Function, ancillary: AncillaryEntityResponse | null) => {
    const yupReservationFrom = yupReservationFromFunc({
        t,
        id: ancillary?.id || null,
        reservationFrom: ancillary?.reservationFrom as string,
    });
    const yupReservationTo = yupReservationToFunc({
        t,
        id: ancillary?.id || null,
        reservationTo: ancillary?.reservationTo as string,
        reservationFromFieldName: "reservationFrom"
    });
    const yupPickupFrom = yupPickupFromFunc({
        t,
        id: ancillary?.id || null,
        pickupFrom: ancillary?.pickupFrom as string,
    });
    const yupPickupTo = yupPickupToFunc({
        t,
        id: ancillary?.id || null,
        reservationTo: ancillary?.reservationTo as string,
        pickupFrom: ancillary?.pickupFrom as string,
        pickupTo: ancillary?.pickupTo as string,
        pickupFromFieldName: "pickupFrom"
    });

    return yup.object().shape({
        dealer: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string(),
            })
            .required(t("translation:validation.fieldMandatory"))
            .nullable(true),
        legalEntity: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string(),
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        segments: yup
            .array()
            .min(1, t("translation:validation.fieldMandatory"))
            .of(
                yup
                    .object()
                    .shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                    .nullable()
                    .required(t("translation:validation.fieldMandatory"))
            ),
        code: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        nameAncillary: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        description: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        toleranceTime: yup
            .number()
            .nullable()
            .integer()
            .min(0, t("pricing:validation.positiveNumber"))
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        reservationFrom: yupReservationFrom,
        reservationTo: yupReservationTo,
        pickupFrom: yupPickupFrom,
        pickupTo: yupPickupTo,
        currency: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        // dealer: yup
        //     .object()
        //     .nullable()
        //     .required(t("translation:validation.fieldMandatory")),
        minimumAmount: yup.number()
            .nullable()
            .min(0, t("pricing:validation.positiveNumber"))
            .integer()
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        maximumAmount: yup.number()
            .nullable()
            .integer()
            .min(yup.ref("minimumAmount"), ({min}) => t("pricing:validation.numberGreaterThan") + min)
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        maxUnits: yup.number()
            .nullable()
            .min(0, t("pricing:validation.positiveNumber"))
            .integer()
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        taxCode: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory"))
    });
};

export const buildEntityRequestFromFormValues = (formValues: FormOWAncillaryModel, ancillaryInUse: boolean, everyLegalEntitiesWithoutAll: string[], dealerFilteredOptions: string[]): OWAncillaryEntityRequest => {
    return {
        prcAncillary: {
            chargeType: formValues.chargeType as string,
            chargingWay: formValues.chargingWay as string,
            currency: formValues.currency as string,
            maxUnits: formValues.maxUnits === 0 ? null : formValues.maxUnits as number,
            note: formValues.note || null,
            pickupFrom: formValues.pickupFrom,
            pickupTo: formValues.pickupTo,
            reservationFrom: formValues.reservationFrom,
            reservationTo: formValues.reservationTo,
            amountMin: formValues.minimumAmount as number > 0 ? Number(i18nConvertNumberWithDecimals(Number(formValues.minimumAmount) * 100)) : null,
            amountMax: formValues.maximumAmount as number > 0 ? Number(i18nConvertNumberWithDecimals(Number(formValues.maximumAmount) * 100)) : null,
            dealerId: 1,
            //dealerId: formValues.dealer?.code as number,
            tpUnit: formValues.tpUnit as string,
            nameAncillary: formValues.nameAncillary,
            code: formValues.code,
            description: formValues.description,
            type: formValues.type as string,
            subtype: formValues.subtype as string,
            segments: fromGenericFeToGenericBe(
                formValues.segments
            ) as SelectElementModelBE[],
            cardinality: formValues.cardinality || null,
            sellDesk: formValues.sellDesk,
            passageToCounterNeeded: formValues.passageToCounterNeeded,
            applicationTiming: formValues.applicationTiming === null ? null : formValues.applicationTiming === "PICK UP" ? "PICK_UP" : "DROP_OFF",
            toleranceTime: formValues.toleranceTime,
            // RF-02 (US-SYS-004) - 5 flag ancillary: non configurabili nello screen OW, default sicuri
            flagResellableAtDeskPostRa: false,
            flagResellableInSubstitution: false,
            flagDiscountApplicable: false,
            timeToChange: null,
            timeToRemove: null,
            additionalField: formValues.additionalField,
            tax: formValues.tax,
            taxCode: typeof formValues.taxCode === 'object' && formValues.taxCode !== null
                ? (formValues.taxCode as unknown as TaxCodeObject).value
                : formValues.taxCode,
            isOneWay: true,
            used: ancillaryInUse as boolean,
            dealer: dealerFilteredOptions.length === 1 ? dealerFilteredOptions[0] : formValues.dealer?.code as string,
            legalEntity: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll[0] : formValues.legalEntity?.code as string
        }
    };
};

export const buildOWAncillaryForm = (
    owAncillary: OWAncillaryEntityResponse,
    t: any,
    services: any
): FormOWAncillaryModel => {

    return {
        id: owAncillary.id,
        note: owAncillary.prcAncillary.note ? owAncillary.prcAncillary.note as string : null,
        pickupFrom: dayjs(owAncillary.prcAncillary.pickupFrom).toISOString(),
        pickupTo: dayjs(owAncillary.prcAncillary.pickupTo).toISOString(),
        reservationFrom: dayjs(owAncillary.prcAncillary.reservationFrom).toISOString(),
        reservationTo: dayjs(owAncillary.prcAncillary.reservationTo).toISOString(),
        tpUnit: owAncillary.prcAncillary.tpUnit as string,
        currency: owAncillary.prcAncillary.currency,
        minimumAmount: owAncillary.prcAncillary.amountMin ? Number(i18nConvertNumberWithDecimals(Number(owAncillary.prcAncillary.amountMin) / 100)) : null,
        maximumAmount: owAncillary.prcAncillary.amountMax ? Number(i18nConvertNumberWithDecimals(Number(owAncillary.prcAncillary.amountMax) / 100)) : null,
        chargingWay: owAncillary.prcAncillary.chargingWay || "",
        maxUnits: owAncillary.prcAncillary.maxUnits,
        chargeType: owAncillary.prcAncillary.chargeType,
        nameAncillary: owAncillary.prcAncillary.nameAncillary, // TODO: implementazione multilanguage?
        code: owAncillary.prcAncillary.code,
        description: owAncillary.prcAncillary.description,
        type: owAncillary.prcAncillary.type,
        subtype: owAncillary.prcAncillary.subtype,
        segments: fromGenericBeToGenericFe(owAncillary.prcAncillary.segments),
        cardinality: owAncillary.prcAncillary.cardinality as number,
        sellDesk: owAncillary.prcAncillary.sellDesk,
        passageToCounterNeeded: owAncillary.prcAncillary.passageToCounterNeeded,
        applicationTiming: owAncillary.prcAncillary.applicationTiming === null ? null : owAncillary.prcAncillary.applicationTiming === "PICK_UP" ? "PICK UP" : "DROP OFF",
        toleranceTime: owAncillary.prcAncillary.toleranceTime,
        additionalField: owAncillary.prcAncillary.additionalField,
        tax: owAncillary.prcAncillary.tax,
        taxCode: services.find((s: any) => s.code === owAncillary.prcAncillary.taxCode)?.name || null,
        dealer: {code: owAncillary.prcAncillary.dealer, label: t('translation:dealers.'.concat(owAncillary.prcAncillary.dealer))},
        legalEntity: {code: owAncillary.prcAncillary.legalEntity, label: t('translation:legalEntities.'.concat(owAncillary.prcAncillary.legalEntity))}
    };
};
